import type { Message } from '../../../contracts/harness.types';
import { userMessageSignature } from './signature';
import type { TranscriptDraft } from './store';
import type { TranscriptStore, UserTranscriptEntry } from './types';

/** Persisted copies often land text-only first; keep optimistic attachment blocks until the BE catches up. */
export const mergePersistedUserMessage = (
  optimistic: Message,
  persisted: Message
): Message => {
  const optimisticAttachments = optimistic.blocks.filter(
    (block) => block.kind === 'attachment'
  );
  if (optimisticAttachments.length === 0) return persisted;

  const persistedAttachmentIds = new Set(
    persisted.blocks
      .filter((block) => block.kind === 'attachment')
      .map((block) => block.attachment_id)
  );
  const attachmentsToKeep = optimisticAttachments.filter(
    (block) => !persistedAttachmentIds.has(block.attachment_id)
  );
  if (attachmentsToKeep.length === 0) return persisted;

  return {
    role: 'user',
    blocks: [...persisted.blocks, ...attachmentsToKeep],
  };
};

export const persistedUserCount = (store: TranscriptStore): number =>
  store.order.reduce((count, entryId) => {
    const entry = store.byId[entryId];
    return entry?.kind === 'user' && entry.source === 'persisted' ? count + 1 : count;
  }, 0);

/**
 * SEAM #1, the other half: folds a fetched copy INTO the optimistic bubble that
 * has no backend key, so the entry on screen survives and only its payload and
 * position are upgraded.
 *
 * The submit-time baseline is a LOWER BOUND, not a slot — two turns minted at
 * the same baseline (a queue drain) would otherwise double-claim it — and each
 * accepted turn takes the earliest copy no earlier turn took. Only an ACCEPTED
 * turn claims, and the persisted text must end with what we sent AT A LINE
 * BOUNDARY: the backend composes the stored message around the submitted
 * prompt (context wrapper, then a blank line, then the prompt verbatim), so
 * equality never holds, while a peer device's message persisted inside our
 * window won't share our tail line. A mismatch leaves both rows up until our
 * own copy arrives — a visible duplicate being the lesser failure.
 */
export const reconcileAcceptedOptimistic = (draft: TranscriptDraft): void => {
  const candidates: PersistedCandidate[] = [];
  const optimistic: string[] = [];
  let slot = 0;
  for (const entryId of draft.order) {
    const entry = draft.byId[entryId];
    if (entry?.kind !== 'user') continue;
    if (entry.source === 'persisted') {
      candidates.push({
        id: entryId,
        slot,
        claimable: !isOptimisticBorn(entry),
        signature: userMessageSignature(entry.message),
      });
      slot += 1;
    } else if (entry.source === 'optimistic' && entry.accepted) {
      optimistic.push(entryId);
    }
  }
  if (candidates.length === 0 || optimistic.length === 0) return;

  const claimed = new Set<string>();
  for (const optimisticId of optimistic) {
    const entry = draft.byId[optimisticId];
    if (entry?.kind !== 'user') continue;
    const minSlot = (entry.baselineUserCount ?? 0) + entry.position;
    const signature = userMessageSignature(entry.message);
    if (!signature) continue;
    const candidate = candidates.find(
      (option) =>
        option.claimable &&
        option.slot >= minSlot &&
        !claimed.has(option.id) &&
        (option.signature === signature || option.signature.endsWith(`\n${signature}`))
    );
    if (!candidate) continue;
    claimed.add(candidate.id);
    absorb(draft, optimisticId, candidate.id);
  }
};

interface PersistedCandidate {
  id: string;
  /** Position among ALL persisted user entries, born ones included. */
  slot: number;
  claimable: boolean;
  signature: string;
}

/**
 * Started as an optimistic bubble, so a LATER turn must never claim it. Only the
 * optimistic mint writes `baselineUserCount`, and every upgrade spreads the
 * survivor, so the marker outlives the promotion to `persisted`.
 */
const isOptimisticBorn = (entry: UserTranscriptEntry): boolean =>
  entry.baselineUserCount !== undefined;

/** The survivor keeps its id (and so its React key) and takes everything else. */
const absorb = (draft: TranscriptDraft, survivorId: string, absorbedId: string): void => {
  const survivor = draft.byId[survivorId];
  const absorbed = draft.byId[absorbedId];
  if (survivor?.kind !== 'user' || absorbed?.kind !== 'user') return;

  const next: UserTranscriptEntry = {
    ...survivor,
    source: 'persisted',
    accepted: true,
    message: mergePersistedUserMessage(survivor.message, absorbed.message),
    ...(absorbed.turnId && { turnId: absorbed.turnId }),
  };
  draft.byId[survivorId] = next;
  delete draft.byId[absorbedId];

  for (const [key, entryId] of Object.entries(draft.keys)) {
    if (entryId === absorbedId) draft.keys[key] = survivorId;
  }

  const absorbedIndex = draft.order.indexOf(absorbedId);
  const survivorIndex = draft.order.indexOf(survivorId);
  const order = draft.order.filter((id) => id !== survivorId && id !== absorbedId);
  order.splice(
    survivorIndex < absorbedIndex ? absorbedIndex - 1 : absorbedIndex,
    0,
    survivorId
  );
  draft.order = order;
  draft.changed = true;
};
